import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { Box, Collapse, IconButton, Stack, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  getSessionDrawerChrome,
  sessionDrawerCardSx,
  sessionDrawerIconButtonSx,
} from '@kangaroo/ui';

import type { LapTimingMode } from '../../features/sessions/useSessionLapSlot';
import { useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import type { CompareSessionGroups, SessionListItem } from '../../features/sessions/sessionList';
import {
  buildCircuitSessionLapOptions,
  fetchSessionExport,
  pickDefaultCompareLapFromOptions,
  pickDefaultCompareLapNumber,
  sessionExportCacheKey,
} from '../../features/sessions/sessionContent';
import { buildCircuitLapReslice } from '../../lib/circuitLapReslice';
import type { CircuitMapOverlay } from '../../lib/circuitMapOverlay';
import type { MyChronSessionExport } from '../../lib/telemetrySession';
import { SessionLapPanel } from './SessionLapPanel';

type SessionCompareSlotPanelProps = {
  publicDemo?: boolean;
  index: number;
  sessionId: string;
  lapNumber: number | null;
  lapTimingMode: LapTimingMode;
  sessionGroups: CompareSessionGroups;
  sessions: SessionListItem[];
  primarySessionId: string | null;
  primarySessionExport?: MyChronSessionExport | null;
  primaryLapNumber: number | null;
  primarySelectedCircuitLap: number | null;
  primaryCircuitOverlay: CircuitMapOverlay | null;
  onSessionChange: (sessionId: string) => void;
  onLapChange: (lapNumber: number) => void;
  onRemove: () => void;
};

export function SessionCompareSlotPanel({
  publicDemo = false,
  index,
  sessionId,
  lapNumber,
  lapTimingMode,
  sessionGroups,
  sessions,
  primarySessionId,
  primarySessionExport = null,
  primaryLapNumber,
  primarySelectedCircuitLap,
  primaryCircuitOverlay,
  onSessionChange,
  onLapChange,
  onRemove,
}: SessionCompareSlotPanelProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const session = sessions.find((entry) => entry.id === sessionId) ?? null;
  const usesPrimarySession = sessionId === primarySessionId;

  const { data: remoteExport, isLoading: remoteLoading } = useSWR(
    sessionId && !usesPrimarySession ? sessionExportCacheKey(sessionId) : null,
    () => fetchSessionExport(sessionId),
    { revalidateOnFocus: false },
  );

  const { data: primaryExport } = useSWR(
    usesPrimarySession && primarySessionId && !primarySessionExport && !publicDemo ? sessionExportCacheKey(primarySessionId) : null,
    () => fetchSessionExport(primarySessionId!),
    { revalidateOnFocus: false },
  );

  const sessionExport = usesPrimarySession ? primarySessionExport ?? primaryExport : remoteExport;
  const sessionLoading = usesPrimarySession ? !sessionExport && Boolean(primarySessionId) : remoteLoading;

  const slot = useSessionLapSlot({
    sessionId: publicDemo ? null : sessionId,
    sessionExport,
    lapTimingMode,
    sessionTrackName: session?.trackName ?? null,
    readOnly: publicDemo,
    publicDemo,
    compareMode: true,
    externalCircuitOverlay: primaryCircuitOverlay,
  });

  const derivedCircuitReslice = useMemo(() => {
    if (
      lapTimingMode !== 'circuit'
      || !sessionExport
      || !primaryCircuitOverlay?.startFinishLine
    ) {
      return null;
    }

    return buildCircuitLapReslice(sessionExport, primaryCircuitOverlay);
  }, [lapTimingMode, primaryCircuitOverlay, sessionExport]);

  const derivedCircuitLaps = useMemo(
    () => buildCircuitSessionLapOptions(derivedCircuitReslice),
    [derivedCircuitReslice],
  );

  const effectiveSlot = useMemo(() => {
    if (lapTimingMode !== 'circuit' || derivedCircuitLaps.length === 0 || !derivedCircuitReslice) {
      return slot;
    }

    return {
      ...slot,
      circuitLaps: derivedCircuitLaps,
      circuitLapReslice: {
        ...slot.circuitLapReslice,
        activeReslice: derivedCircuitReslice,
      },
      hasCircuitOverlay: true,
      contributionsLoading: false,
    };
  }, [derivedCircuitLaps, derivedCircuitReslice, lapTimingMode, slot]);

  useEffect(() => {
    if (lapNumber !== null || !sessionExport) {
      return;
    }

    if (lapTimingMode === 'circuit') {
      if (effectiveSlot.circuitLaps.length === 0) {
        return;
      }

      const defaultLap = pickDefaultCompareLapFromOptions(effectiveSlot.circuitLaps, index);

      if (defaultLap != null) {
        effectiveSlot.circuitLapReslice.setSelectedLapNumber(defaultLap);
        onLapChange(defaultLap);
      }
      return;
    }

    const defaultLap = pickDefaultCompareLapNumber(sessionExport, index);

    if (defaultLap != null) {
      onLapChange(defaultLap);
    }
  }, [
    index,
    lapNumber,
    lapTimingMode,
    onLapChange,
    sessionExport,
    effectiveSlot.circuitLaps,
    effectiveSlot.circuitLapReslice,
  ]);

  const handleLapChange = (nextLapNumber: number) => {
    if (lapTimingMode === 'circuit') {
      effectiveSlot.circuitLapReslice.setSelectedLapNumber(nextLapNumber);
    }

    onLapChange(nextLapNumber);
  };

  const [expanded, setExpanded] = useState(true);

  const selectedLapLabel = useMemo(() => {
    const selectedLapNumber =
      lapTimingMode === 'circuit' ? effectiveSlot.selectedLapNumber : lapNumber;
    const laps = lapTimingMode === 'circuit' ? effectiveSlot.circuitLaps : effectiveSlot.laps;
    return laps.find((lap) => lap.selectable && lap.lapNumber === selectedLapNumber)?.rowLabel ?? null;
  }, [effectiveSlot.circuitLaps, effectiveSlot.laps, effectiveSlot.selectedLapNumber, lapNumber, lapTimingMode]);

  const collapsedSummary = session
    ? selectedLapLabel
      ? `${session.title} · ${selectedLapLabel}`
      : session.title
    : 'Choose session';

  return (
    <Stack
      spacing={0}
      sx={{
        p: 1.25,
        ...sessionDrawerCardSx(isDark),
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={0.5}
        onClick={() => setExpanded((value) => !value)}
        sx={{ minWidth: 0, cursor: 'pointer', userSelect: 'none' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 800,
              color: expanded ? chrome.collapsibleTitle : chrome.collapsibleTitleCollapsed,
              letterSpacing: '0.04em',
            }}
          >
            COMPARE LAP {index + 1}
          </Typography>
          {!expanded ? (
            <Typography sx={{ mt: 0.25, fontSize: '0.72rem', color: chrome.collapsibleSummary, lineHeight: 1.35 }} noWrap>
              {collapsedSummary}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.35} sx={{ flexShrink: 0 }}>
          <ExpandMoreIcon
            aria-hidden
            sx={{
              fontSize: 20,
              color: chrome.collapsibleIcon,
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 160ms ease',
            }}
          />
          <IconButton
            aria-label={`Remove compare lap ${index + 1}`}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            sx={{
              ...sessionDrawerIconButtonSx(isDark),
              p: 0.35,
              '&:hover': { color: chrome.removeHoverText, bgcolor: chrome.removeHoverBg },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={expanded} unmountOnExit={false}>
        <Box sx={{ mt: 1.25 }} onClick={(event) => event.stopPropagation()}>
          <SessionLapPanel
            sessionSelectLabel="Compare session"
            sessionGroups={sessionGroups}
            sessionId={sessionId}
            onSessionChange={onSessionChange}
            sessionLoading={sessionLoading}
            lapTimingMode={lapTimingMode}
            slot={effectiveSlot}
            mychronLapNumber={lapNumber}
            onMychronLapChange={handleLapChange}
            selectionMode="compare"
          />
        </Box>
      </Collapse>
    </Stack>
  );
}
